/**
 * ============================================================
 * CDS — Atelier Bijou (JS externalise)
 * Studio  : Chlo' Design Studio
 * Version : 1.1
 * Desc.   : Custom element <cds-atelier-bijou> de la section
 *           sections/cds-atelier-bijou.liquid. Deux etapes : choix
 *           de la base (chaine, creoles) puis charms poses sur la
 *           photo de la base (drag souris et tactile via Pointer
 *           Events, clavier, undo, persistance 7 jours), choix de
 *           variante explicite, controle de stock avant ajout,
 *           ajout panier groupe (_bundle_group / _bundle_role,
 *           regroupe par snippets/cart-products.liquid), tiroir
 *           panier Horizon via assets/cds-builders-common.js.
 *           Config par instance : <script type="application/json"
 *           data-ab-config> a l'interieur de l'element.
 *           Cadrage automatique (v1.1) : le bijou est detecte sur la
 *           photo de base (detourage ou fond uni) puis centre dans une
 *           zone fixe du canvas ; points d'accroche detectes en bas de
 *           chaque piece (anneaux d'une paire de creoles) pour poser
 *           les charms a l'ajout.
 *           Depend de cds-money.js et cds-builders-common.js
 *           (charges par la section avant ce fichier).
 * ============================================================
 */
(function () {
  'use strict';
  if (customElements.get('cds-atelier-bijou')) return;

  var STORAGE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
  var STORAGE_VERSION = 1;
  var HISTORY_MAX = 20;
  var HINT_KEY = 'cds_ab_hint_seen';
  var BOUNDS_CACHE = {};
  var FRAME_CACHE = {};
  var FRAME_SIZE = 320;

  /* ---------------------------------------------------------- */
  /* Utilitaires                                                 */
  /* ---------------------------------------------------------- */
  function formatMoney(cents, mf) {
    cents = parseInt(cents, 10);
    if (isNaN(cents)) cents = 0;
    if (window.CDSMoney && typeof window.CDSMoney.format === 'function') {
      return window.CDSMoney.format(cents, mf);
    }
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  }

  function rafThrottle(fn) {
    var pending = false;
    return function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        fn();
      });
    };
  }

  function debounce(fn, ms) {
    var t;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  function haptic(pattern) {
    try {
      if (navigator && typeof navigator.vibrate === 'function') navigator.vibrate(pattern || 10);
    } catch (e) { /* silencieux */ }
  }

  function uid() {
    return 'c' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
  }

  function clamp(v, min, max) {
    return Math.max(min, Math.min(max, v));
  }

  function round2(v) {
    return Math.round(v * 100) / 100;
  }

  function base64url(str) {
    return btoa(unescape(encodeURIComponent(str)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  function sizedImg(src, size) {
    if (!src) return '';
    if (src.indexOf('data:') === 0) return src;
    if (window.CDSBuilders && typeof window.CDSBuilders.sizedImg === 'function') {
      return window.CDSBuilders.sizedImg(src, size);
    }
    return src;
  }

  /* Taille d'un onglet (25..100) -> largeur du charm en % du canvas (10..40).
     Independant de l'ecran : le canvas est toujours carre. */
  function sizeToCanvasPct(setting) {
    var p = parseInt(setting, 10);
    if (isNaN(p)) p = 70;
    p = clamp(p, 25, 100);
    return round2(10 + (p - 25) * 0.4);
  }

  function parseVariants(el) {
    try {
      var arr = JSON.parse(el.dataset.variants || '[]');
      return Array.isArray(arr) ? arr : [];
    } catch (e) {
      return [];
    }
  }

  /* Carte (base ou charm) -> objet produit normalise */
  function parseProduct(el) {
    if (!el) return null;
    var variants = parseVariants(el);
    if (!variants.length) {
      variants = [{
        id: parseInt(el.dataset.variantId, 10),
        title: '',
        price: parseInt(el.dataset.price, 10) || 0,
        available: el.dataset.available === 'true',
        image: null
      }];
    }
    return {
      id: parseInt(el.dataset.productId, 10),
      handle: el.dataset.handle || '',
      title: el.dataset.title || '',
      image: el.dataset.image || '',
      price: parseInt(el.dataset.price, 10) || 0,
      available: el.dataset.available === 'true',
      multi: el.dataset.multi === 'true',
      variantId: parseInt(el.dataset.variantId, 10),
      variants: variants
    };
  }

  function findVariant(product, id) {
    id = parseInt(id, 10);
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].id === id) return product.variants[i];
    }
    return null;
  }

  function firstAvailable(product, preferredId) {
    var v = preferredId ? findVariant(product, preferredId) : null;
    if (v && v.available) return v;
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].available) return product.variants[i];
    }
    return null;
  }

  /* Boite englobante des pixels visibles d'un PNG transparent :
     la zone cliquable et la croix collent au charm visible. */
  function computeImageBounds(url) {
    if (!url) return Promise.resolve(null);
    if (Object.prototype.hasOwnProperty.call(BOUNDS_CACHE, url)) {
      return Promise.resolve(BOUNDS_CACHE[url]);
    }
    return new Promise(function (resolve) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        try {
          var maxDim = 160;
          var scale = Math.min(1, maxDim / Math.max(img.width, img.height));
          var w = Math.max(8, Math.round(img.width * scale));
          var h = Math.max(8, Math.round(img.height * scale));
          var c = document.createElement('canvas');
          c.width = w;
          c.height = h;
          var ctx = c.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          var data = ctx.getImageData(0, 0, w, h).data;
          var minX = w, minY = h, maxX = -1, maxY = -1;
          for (var y = 0; y < h; y++) {
            for (var x = 0; x < w; x++) {
              if (data[(y * w + x) * 4 + 3] > 16) {
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
              }
            }
          }
          var bounds = null;
          if (maxX >= minX && maxY >= minY) {
            bounds = {
              x: (minX / w) * 100,
              y: (minY / h) * 100,
              w: ((maxX - minX + 1) / w) * 100,
              h: ((maxY - minY + 1) / h) * 100,
              ar: img.height / img.width
            };
            /* Une image sans transparence (JPG) ne se rogne pas */
            if (bounds.w > 98 && bounds.h > 98) bounds = null;
          }
          BOUNDS_CACHE[url] = bounds;
          resolve(bounds);
        } catch (e) {
          BOUNDS_CACHE[url] = null;
          resolve(null);
        }
      };
      img.onerror = function () {
        BOUNDS_CACHE[url] = null;
        resolve(null);
      };
      img.src = url;
    });
  }

  function median(arr) {
    if (!arr.length) return 0;
    var a = arr.slice().sort(function (x, y) { return x - y; });
    return a[Math.floor(a.length / 2)];
  }

  /* Version legere de la photo pour l'analyse (meme ratio) */
  function analysisUrl(src) {
    if (!src || src.indexOf('data:') === 0) return src;
    if (/[?&]width=\d+/.test(src)) return src.replace(/([?&]width=)\d+/, function (m, p1) { return p1 + '480'; });
    return src;
  }

  /* Analyse d'une photo de base (image reduite a 320 px).
     1. Fond : bord transparent (photo detouree) ou couleur mediane du bord.
     2. Niveaux : 0 fond, 1 contenu leger (ombre portee, halo, reflet tres
        clair), 2 contenu net. Sur fond uni, si le bijou est colore (or, or
        rose), les gris neutres plus sombres que le fond passent en leger :
        ce sont des ombres.
     3. Zones connexes : une poussiere ou un residu de detourage (moins de
        2 % de la plus grande zone) est ignore.
     4. Boite du bijou, puis pieces : une paire de creoles est coupee sur
        des colonnes sans contenu net, ou, si les anneaux se touchent, au
        point haut entre leurs deux bas. Pour chaque piece : profil du bas
        (premier contenu depuis le bas, contenu net juste au-dessus s'il
        existe, pour franchir une ombre), point d'accroche au milieu.
     null si rien de fiable n'est detecte. */
  function analyseJewel(img) {
    var iw = img.naturalWidth || img.width;
    var ih = img.naturalHeight || img.height;
    if (!iw || !ih) return null;
    var sc = Math.min(1, FRAME_SIZE / Math.max(iw, ih));
    var w = Math.max(16, Math.round(iw * sc));
    var h = Math.max(16, Math.round(ih * sc));
    var cv = document.createElement('canvas');
    cv.width = w;
    cv.height = h;
    var ctx = cv.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, w, h);
    var d = ctx.getImageData(0, 0, w, h).data;
    var n = w * h;

    var edgeCount = 0, edgeClear = 0, rs = [], gs = [], bs = [];
    function edge(x, y) {
      var i = (y * w + x) * 4;
      edgeCount++;
      if (d[i + 3] < 250) edgeClear++;
      rs.push(d[i]); gs.push(d[i + 1]); bs.push(d[i + 2]);
    }
    for (var ex = 0; ex < w; ex++) { edge(ex, 0); edge(ex, h - 1); }
    for (var ey = 1; ey < h - 1; ey++) { edge(0, ey); edge(w - 1, ey); }
    var alphaMode = edgeClear > edgeCount * 0.5;
    var br = median(rs), bgc = median(gs), bb = median(bs);
    var bgLum = (br + bgc + bb) / 3;

    var lvl = new Uint8Array(n);
    var chromas = [];
    var p, k;
    for (p = 0; p < n; p++) {
      k = p * 4;
      var a = d[k + 3];
      var v = 0;
      if (alphaMode) {
        v = a > 160 ? 2 : (a > 40 ? 1 : 0);
      } else if (a > 40) {
        var diff = Math.abs(d[k] - br) + Math.abs(d[k + 1] - bgc) + Math.abs(d[k + 2] - bb);
        v = diff > 105 ? 2 : (diff > 42 ? 1 : 0);
        if (v === 2 && p % 5 === 0) {
          chromas.push(Math.max(d[k], d[k + 1], d[k + 2]) - Math.min(d[k], d[k + 1], d[k + 2]));
        }
      }
      lvl[p] = v;
    }
    if (!alphaMode && chromas.length > 20 && median(chromas) > 40) {
      for (p = 0; p < n; p++) {
        if (lvl[p] !== 2) continue;
        k = p * 4;
        var mx = Math.max(d[k], d[k + 1], d[k + 2]);
        var mn = Math.min(d[k], d[k + 1], d[k + 2]);
        if (mx - mn < 18 && (d[k] + d[k + 1] + d[k + 2]) / 3 < bgLum) lvl[p] = 1;
      }
    }

    /* Zones connexes (8 voisins) */
    var comp = new Int32Array(n);
    var stack = new Int32Array(n);
    var areas = [0];
    var label = 0;
    for (p = 0; p < n; p++) {
      if (!lvl[p] || comp[p]) continue;
      label++;
      var area = 0, sp = 0;
      comp[p] = label;
      stack[sp++] = p;
      while (sp) {
        var q = stack[--sp];
        area++;
        var qx = q % w, qy = (q - qx) / w;
        for (var dy = -1; dy <= 1; dy++) {
          var ny = qy + dy;
          if (ny < 0 || ny >= h) continue;
          for (var dx = -1; dx <= 1; dx++) {
            var nx = qx + dx;
            if ((dx === 0 && dy === 0) || nx < 0 || nx >= w) continue;
            var nq = ny * w + nx;
            if (lvl[nq] && !comp[nq]) { comp[nq] = label; stack[sp++] = nq; }
          }
        }
      }
      areas.push(area);
    }
    if (!label) return null;
    var maxArea = 0;
    for (var li = 1; li <= label; li++) if (areas[li] > maxArea) maxArea = areas[li];
    var keepMin = Math.max(3, maxArea * 0.02);
    var keep = new Uint8Array(label + 1);
    for (li = 1; li <= label; li++) keep[li] = areas[li] >= keepMin ? 1 : 0;
    function on(q) { return lvl[q] && keep[comp[q]]; }
    function hard(q) { return lvl[q] === 2 && keep[comp[q]]; }

    var colCount = new Uint16Array(w), rowCount = new Uint16Array(h), colHard = new Uint16Array(w);
    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        var qq = y * w + x;
        if (!on(qq)) continue;
        colCount[x]++;
        rowCount[y]++;
        if (lvl[qq] === 2) colHard[x]++;
      }
    }
    var x0 = 0, x1 = w - 1, y0 = 0, y1 = h - 1;
    while (x0 < w && !colCount[x0]) x0++;
    while (x1 >= 0 && !colCount[x1]) x1--;
    while (y0 < h && !rowCount[y0]) y0++;
    while (y1 >= 0 && !rowCount[y1]) y1--;
    if (x1 <= x0 || y1 <= y0) return null;
    var bw = (x1 - x0 + 1) / w;
    var bh = (y1 - y0 + 1) / h;
    if ((bw > 0.97 && bh > 0.97) || bw < 0.04 || bh < 0.04) return null;

    var win = Math.max(2, Math.round(h * 0.05));
    function bottomAt(px) {
      for (var py = y1; py >= y0; py--) {
        var q0 = py * w + px;
        if (!on(q0)) continue;
        if (lvl[q0] === 2) return py;
        var lim = Math.max(y0, py - win);
        for (var py2 = py - 1; py2 >= lim; py2--) {
          if (hard(py2 * w + px)) return py2;
        }
        return py;
      }
      return -1;
    }
    var bottoms = new Int32Array(w).fill(-1);
    for (var bx = x0; bx <= x1; bx++) bottoms[bx] = bottomAt(bx);

    /* Coupure d'une paire */
    var cutA = -1, cutB = -1;
    var m0 = x0 + Math.round((x1 - x0) * 0.25);
    var m1 = x0 + Math.round((x1 - x0) * 0.75);
    var best = 0, run = -1;
    for (var gx = m0; gx <= m1; gx++) {
      if (!colHard[gx]) {
        if (run < 0) run = gx;
        if (gx - run + 1 > best) { best = gx - run + 1; cutA = run; cutB = gx; }
      } else {
        run = -1;
      }
    }
    if (best < 2) {
      cutA = cutB = -1;
      var mid = Math.round((x0 + x1) / 2);
      var lowL = -1, lowR = -1;
      for (var lx = x0; lx < mid; lx++) if (bottoms[lx] >= 0 && (lowL < 0 || bottoms[lx] > bottoms[lowL])) lowL = lx;
      for (var rx = mid; rx <= x1; rx++) if (bottoms[rx] >= 0 && (lowR < 0 || bottoms[rx] >= bottoms[lowR])) lowR = rx;
      if (lowL >= 0 && lowR >= 0 && lowR - lowL > (x1 - x0) * 0.25) {
        var highM = -1;
        for (var mx2 = lowL + 1; mx2 < lowR; mx2++) {
          if (bottoms[mx2] >= 0 && (highM < 0 || bottoms[mx2] < bottoms[highM])) highM = mx2;
        }
        var dip = (y1 - y0) * 0.08;
        if (highM >= 0 && bottoms[lowL] - bottoms[highM] > dip && bottoms[lowR] - bottoms[highM] > dip) {
          cutA = cutB = highM;
        }
      }
    }
    var parts = [[x0, x1]];
    if (cutA >= 0) {
      var massL = 0, massR = 0;
      for (var cx = x0; cx < cutA; cx++) massL += colHard[cx];
      for (cx = cutB + 1; cx <= x1; cx++) massR += colHard[cx];
      if (Math.min(massL, massR) >= Math.max(massL, massR) * 0.3) {
        parts = [[x0, cutA - 1], [cutB + 1, x1]];
      }
    }

    var pieces = [];
    parts.forEach(function (pt) {
      var profile = [];
      var sx0 = -1, sx1 = -1, top = -1;
      for (var px = pt[0]; px <= pt[1]; px++) {
        if (colHard[px]) {
          if (sx0 < 0) sx0 = px;
          sx1 = px;
        }
        if (bottoms[px] < 0) continue;
        profile.push([(px + 0.5) / w, (bottoms[px] + 1) / h]);
        for (var ty = y0; ty <= y1; ty++) {
          if (on(ty * w + px)) { if (top < 0 || ty < top) top = ty; break; }
        }
      }
      if (!profile.length) return;
      /* Point d'accroche : milieu des colonnes les plus basses de la piece
         (bas de l'anneau), a une tolerance pres pour ne pas dependre d'une
         seule colonne. */
      var maxB = -1;
      for (var bx2 = pt[0]; bx2 <= pt[1]; bx2++) if (bottoms[bx2] > maxB) maxB = bottoms[bx2];
      var tol = Math.max(1, Math.round(h * 0.015));
      var lows = [];
      for (bx2 = pt[0]; bx2 <= pt[1]; bx2++) if (bottoms[bx2] >= 0 && bottoms[bx2] >= maxB - tol) lows.push(bx2);
      var ax = lows.length ? median(lows) + 0.5 : (sx0 >= 0 ? (sx0 + sx1 + 1) / 2 : (pt[0] + pt[1] + 1) / 2);
      var half = Math.max(1, Math.round((pt[1] - pt[0] + 1) * 0.05));
      var ys = [];
      for (var ix = Math.floor(ax) - half; ix <= Math.floor(ax) + half; ix++) {
        if (ix >= pt[0] && ix <= pt[1] && bottoms[ix] >= 0) ys.push(bottoms[ix] + 1);
      }
      if (!ys.length) ys.push(profile[Math.floor(profile.length / 2)][1] * h);
      pieces.push({
        x: ax / w,
        y: median(ys) / h,
        top: (top >= 0 ? top : y0) / h,
        profile: profile
      });
    });

    return {
      ar: ih / iw,
      opaque: !alphaMode,
      bgMin: Math.min(br, bgc, bb),
      box: { x0: x0 / w, y0: y0 / h, x1: (x1 + 1) / w, y1: (y1 + 1) / h },
      split: parts.length === 2 ? (parts[0][1] + 1 + parts[1][0]) / 2 / w : null,
      pieces: parts.length === 2 && pieces.length === 2 ? pieces : pieces.slice(0, 1)
    };
  }

  function detectJewel(url) {
    if (!url) return Promise.resolve(null);
    if (Object.prototype.hasOwnProperty.call(FRAME_CACHE, url)) return FRAME_CACHE[url];
    FRAME_CACHE[url] = new Promise(function (resolve) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        try { resolve(analyseJewel(img)); } catch (e) { resolve(null); }
      };
      img.onerror = function () { resolve(null); };
      img.src = analysisUrl(url);
    });
    return FRAME_CACHE[url];
  }

  /* Analyse -> position de la photo dans le canvas carre (en %) : le
     bijou tient dans la zone reglee (largeur et hauteur maximales),
     centre horizontalement, son haut a la marge reglee. Pieces, profils
     et coupure de la paire suivent la meme transformation. lift : photo
     opaque sur fond clair, facteur d'eclaircissement qui ramene ce fond
     au blanc avant le fondu dans le canvas (0 : pas de fondu). */
  function computeFrame(res, af) {
    if (!res || !res.box) return null;
    af = af || {};
    var maxW = clamp(parseFloat(af.w) || 80, 10, 100) / 100;
    var maxH = clamp(parseFloat(af.h) || 50, 10, 100) / 100;
    var top = clamp(isNaN(parseFloat(af.t)) ? 20 : parseFloat(af.t), 0, 90) / 100;
    var b = res.box;
    var bw = b.x1 - b.x0;
    var bh = b.y1 - b.y0;
    var wi = Math.min(maxW / bw, maxH / (bh * res.ar));
    var hi = wi * res.ar;
    var left = 0.5 - (b.x0 + bw / 2) * wi;
    var tp = top - b.y0 * hi;
    function X(fx) { return round2((left + fx * wi) * 100); }
    function Y(fy) { return round2((tp + fy * hi) * 100); }
    var lift = 0;
    if (res.opaque && res.bgMin >= 200) lift = round2(Math.min(1.25, 255 / Math.max(1, res.bgMin)));
    return {
      l: round2(left * 100),
      t: round2(tp * 100),
      w: round2(wi * 100),
      h: round2(hi * 100),
      opaque: !!res.opaque,
      lift: lift,
      split: res.split === null || res.split === undefined ? null : X(res.split),
      pieces: (res.pieces || []).map(function (pc) {
        return {
          x: X(pc.x),
          y: Y(pc.y),
          top: Y(pc.top),
          profile: pc.profile.map(function (q) { return [X(q[0]), Y(q[1])]; })
        };
      })
    };
  }

  function svg(paths) {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + paths + '</svg>';
  }
  var ICON_CLOSE = svg('<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>');

  /* ---------------------------------------------------------- */
  /* Custom element                                              */
  /* ---------------------------------------------------------- */
  class CdsAtelierBijou extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this._init();
    }

    disconnectedCallback() {
      this._ready = false;
      if (this._onDocKeydown) document.removeEventListener('keydown', this._onDocKeydown);
      if (this._canvasIO) { try { this._canvasIO.disconnect(); } catch (e) { /* silencieux */ } }
      if (this._fsOpen) this._closeFullscreen();
    }

    _init() {
      var self = this;
      var cfgEl = this.querySelector('[data-ab-config]');
      var cfg = {};
      try { cfg = JSON.parse(cfgEl ? cfgEl.textContent : '{}'); } catch (e) { cfg = {}; }
      cfg.parent = cfg.parent || {};
      cfg.props = cfg.props || {};
      cfg.msg = cfg.msg || {};
      this.cfg = cfg;
      this.parentPrice = parseInt(cfg.parent.price, 10) || 0;
      this.maxCharms = parseInt(cfg.maxCharms, 10) || 99;
      this.storageKey = 'cds_ab_' + (cfg.parent.id || 'x');
      this.section = this.closest('.cds-ab') || this;
      this._auto = cfg.baseFit === 'auto';
      this._hang = this._auto && cfg.placement === 'hang';
      this._frame = null;
      this._frameToken = 0;

      this.state = { base: null, charms: [], note: '' };
      this.step = 1;
      this.history = [];
      this._pendingCharms = null;
      this._lastTotal = this.parentPrice;
      this._loadedGrids = {};

      this._bindElements();
      this._bindEvents();
      this._restoreFromStorage();
      this._refreshUndo();
      this._renderRecap();
      this._renderTotal(true);
      this._updateNoteCounter();

      var firstGrid = this.querySelector('[data-ab-grid].is-active');
      if (firstGrid) this._ensureGridLoaded(firstGrid);
    }

    _money(cents) {
      return formatMoney(cents, this.cfg.moneyFormat);
    }

    _bindElements() {
      this.$steps = this.querySelectorAll('[data-ab-step]');
      this.$fill = this.querySelector('[data-ab-fill]');
      this.$panes = this.querySelectorAll('[data-pane]');
      this.$basesWrap = this.querySelector('[data-ab-bases]');
      this.$bases = this.querySelectorAll('[data-ab-base]');
      this.$next = this.querySelector('[data-ab-next]');
      this.$back = this.querySelector('[data-ab-back]');
      this.$canvas = this.querySelector('[data-ab-canvas]');
      this.$canvasBase = this.querySelector('[data-ab-canvas-base]');
      this.$layer = this.querySelector('[data-ab-layer]');
      this.$hint = this.querySelector('[data-ab-hint]');
      this.$loader = this.querySelector('[data-ab-loader]');
      this.$instruction = this.querySelector('[data-ab-instruction]');
      this.$baseSummary = this.querySelector('[data-ab-base-summary]');
      this.$charmsCount = this.querySelector('[data-ab-charms-count]');
      this.$undo = this.querySelector('[data-ab-undo]');
      this.$fullscreen = this.querySelector('[data-ab-fullscreen]');
      this.$clear = this.querySelector('[data-ab-clear]');
      this.$order = this.querySelector('[data-ab-order]');
      this.$orderRow = this.querySelector('[data-ab-order-row]');
      this.$total = this.querySelector('[data-ab-total]');
      this.$totalBtn = this.querySelector('[data-ab-total-btn]');
      this.$breakdown = this.querySelector('[data-ab-breakdown]');
      this.$details = this.querySelector('[data-ab-details]');
      this.$detailsCount = this.querySelector('[data-ab-details-count]');
      this.$recapList = this.querySelector('[data-ab-recap-list]');
      this.$note = this.querySelector('[data-ab-note]');
      this.$noteCounter = this.querySelector('[data-ab-note-counter]');
      this.$noteSaved = this.querySelector('[data-ab-note-saved]');
      this.$tabs = this.querySelectorAll('[data-ab-tab]');
      this.$grids = this.querySelectorAll('[data-ab-grid]');
      this.$gridsWrap = this.querySelector('[data-ab-grids]');
      this.$search = this.querySelector('[data-ab-search]');
      this.$searchEmpty = this.querySelector('[data-ab-search-empty]');
      this.$submit = this.querySelector('[data-ab-submit]');
      this.$error = this.querySelector('[data-ab-error]');
      this.$success = this.querySelector('[data-ab-success]');
      this.$vBackdrop = this.querySelector('[data-ab-vmodal-backdrop]');
      this.$vModal = this.querySelector('[data-ab-vmodal]');
      this.$vTitle = this.querySelector('[data-ab-vmodal-title]');
      this.$vImg = this.querySelector('[data-ab-vmodal-img]');
      this.$vPrice = this.querySelector('[data-ab-vmodal-price]');
      this.$vOptions = this.querySelector('[data-ab-vmodal-options]');
      this.$vConfirm = this.querySelector('[data-ab-vmodal-confirm]');
      this.$vConfirmText = this.querySelector('[data-ab-vmodal-confirm-text]');
      this.$vClose = this.querySelector('[data-ab-vmodal-close]');
      this.$float = this.querySelector('[data-ab-float]');
      this.$floatCount = this.querySelector('[data-ab-float-count]');
      this.$fs = this.querySelector('[data-ab-fs]');
      this.$fsInner = this.querySelector('[data-ab-fs-inner]');
      this.$fsClose = this.querySelector('[data-ab-fs-close]');
    }

    _bindEvents() {
      var self = this;

      /* Bases : selection et variantes */
      if (this.$basesWrap) {
        this.$basesWrap.addEventListener('click', function (e) {
          var pill = e.target.closest('[data-ab-variant]');
          if (pill) {
            e.stopPropagation();
            var pcard = pill.closest('[data-ab-base]');
            if (pcard && !pill.disabled) self._selectBaseVariant(pcard, pill.dataset.abVariant);
            return;
          }
          var card = e.target.closest('[data-ab-base]');
          if (card) self._selectBase(card, false);
        });
        this.$basesWrap.addEventListener('keydown', function (e) {
          var card = e.target.closest('[data-ab-base]');
          if (!card || e.target !== card) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            self._selectBase(card, false);
          } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            e.preventDefault();
            var cards = Array.prototype.slice.call(self.$bases);
            var idx = cards.indexOf(card);
            var dir = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : -1;
            var nxt = cards[(idx + dir + cards.length) % cards.length];
            if (nxt) nxt.focus();
          }
        });
      }

      /* Navigation entre etapes */
      if (this.$next) this.$next.addEventListener('click', function () { self._goToStep(2); });
      if (this.$back) this.$back.addEventListener('click', function () { self._goToStep(1); });
      this.$steps.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var target = parseInt(btn.dataset.abStep, 10);
          if (target < self.step || btn.classList.contains('is-done')) self._goToStep(target);
        });
      });

      /* Onglets */
      this.$tabs.forEach(function (tab) {
        tab.addEventListener('click', function () { self._activateTab(tab.dataset.abTab, false); });
        tab.addEventListener('keydown', function (e) {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          e.preventDefault();
          var tabs = Array.prototype.slice.call(self.$tabs);
          var idx = tabs.indexOf(tab);
          var nxt = tabs[(idx + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length];
          if (nxt) self._activateTab(nxt.dataset.abTab, true);
        });
      });

      /* Charms : delegation (cartes rendues par Liquid ou ajoutees en JS) */
      if (this.$gridsWrap) {
        this.$gridsWrap.addEventListener('click', function (e) {
          var card = e.target.closest('[data-ab-charm]');
          if (card) self._onCharmClick(card);
        });
      }

      /* Recherche */
      if (this.$search) {
        this.$search.addEventListener('input', debounce(function (e) {
          self._filterCharms(e.target.value);
        }, 120));
      }

      /* Actions canvas */
      if (this.$undo) this.$undo.addEventListener('click', function () { self._undo(); });
      if (this.$clear) {
        this.$clear.addEventListener('click', function () {
          if (!self.state.charms.length) return;
          if (!window.confirm(self.cfg.msg.clearConfirm || 'Effacer tous les charms ?')) return;
          self._pushHistory();
          self.state.charms = [];
          self._renderAll();
          self._save();
        });
      }
      if (this.$fullscreen) this.$fullscreen.addEventListener('click', function () { self._openFullscreen(); });

      /* Mobile : le canvas sort de l'ecran quand on pioche dans la grille.
         Un bouton flottant ramene au bijou ; la bulle d'aide n'est montree
         que si le canvas est visible. */
      this._canvasVisible = true;
      if (this.$canvas && 'IntersectionObserver' in window) {
        this._canvasIO = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) { self._canvasVisible = en.isIntersecting; });
          self._updateFloat();
        }, { threshold: 0.2 });
        this._canvasIO.observe(this.$canvas);
      }
      if (this.$float) {
        this.$float.addEventListener('click', function () {
          if (!self.$canvas) return;
          var header = 0;
          try { header = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height'), 10) || 0; } catch (e) { header = 0; }
          var top = self.$canvas.getBoundingClientRect().top + window.pageYOffset - header - 12;
          try { window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' }); } catch (e) { window.scrollTo(0, Math.max(0, top)); }
        });
      }
      if (this.$fsClose) this.$fsClose.addEventListener('click', function () { self._closeFullscreen(); });
      if (this.$fs) {
        this.$fs.addEventListener('click', function (e) {
          if (e.target === self.$fs) self._closeFullscreen();
        });
      }

      /* Note */
      if (this.$note) {
        this._debouncedNoteSave = debounce(function () {
          self._save();
          if (self.$noteSaved) {
            self.$noteSaved.classList.add('is-visible');
            clearTimeout(self._noteSavedTimer);
            self._noteSavedTimer = setTimeout(function () { self.$noteSaved.classList.remove('is-visible'); }, 1800);
          }
        }, 450);
        this.$note.addEventListener('input', function (e) {
          self.state.note = e.target.value;
          self._updateNoteCounter();
          self._debouncedNoteSave();
        });
      }

      if (this.$details) {
        this.$details.addEventListener('toggle', function () { self.$details.dataset.userToggled = '1'; });
      }

      /* Ajout panier */
      if (this.$submit) this.$submit.addEventListener('click', function () { self._addToCart(); });

      /* Modale variantes */
      if (this.$vClose) this.$vClose.addEventListener('click', function () { self._closeVariantModal(); });
      if (this.$vBackdrop) this.$vBackdrop.addEventListener('click', function () { self._closeVariantModal(); });
      if (this.$vOptions) {
        this.$vOptions.addEventListener('click', function (e) {
          var pill = e.target.closest('[data-ab-vopt]');
          if (!pill || pill.disabled) return;
          self._selectModalVariant(pill.dataset.abVopt);
        });
      }
      if (this.$vConfirm) this.$vConfirm.addEventListener('click', function () { self._confirmModalVariant(); });

      /* Clavier global : Echap ferme, Ctrl/Cmd+Z annule a l'etape 2 */
      this._onDocKeydown = function (e) {
        if (e.key === 'Escape') {
          if (self._fsOpen) { self._closeFullscreen(); return; }
          if (self._vOpen) { self._closeVariantModal(); return; }
        }
        if (self.step !== 2) return;
        var t = e.target;
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
        if ((e.ctrlKey || e.metaKey) && !e.shiftKey && (e.key === 'z' || e.key === 'Z')) {
          e.preventDefault();
          self._undo();
        }
      };
      document.addEventListener('keydown', this._onDocKeydown);
    }

    /* ---------------------------------------------------------- */
    /* Messages                                                    */
    /* ---------------------------------------------------------- */
    _showError(msg) {
      if (!this.$error) return;
      this.$error.textContent = msg;
      this.$error.hidden = false;
      if (this.$success) this.$success.hidden = true;
      clearTimeout(this._errTimer);
      var self = this;
      this._errTimer = setTimeout(function () { self.$error.hidden = true; }, 6000);
    }

    _showSuccess(msg, ms) {
      if (!this.$success) return;
      this.$success.textContent = msg;
      this.$success.hidden = false;
      if (this.$error) this.$error.hidden = true;
      clearTimeout(this._okTimer);
      var self = this;
      this._okTimer = setTimeout(function () { self.$success.hidden = true; }, ms || 4000);
    }

    _hideMessages() {
      if (this.$error) this.$error.hidden = true;
      if (this.$success) this.$success.hidden = true;
    }

    /* ---------------------------------------------------------- */
    /* Etape 1 : base                                              */
    /* ---------------------------------------------------------- */
    _selectBase(card, silent) {
      if (!card || card.classList.contains('is-out-of-stock')) return;
      var product = parseProduct(card);
      if (!product) return;
      var variant = firstAvailable(product, card.dataset.variantId);
      if (!variant) return;

      this.$bases.forEach(function (c) {
        var on = c === card;
        c.classList.toggle('is-active', on);
        c.setAttribute('aria-checked', on ? 'true' : 'false');
        c.setAttribute('tabindex', on ? '0' : '-1');
      });
      card.dataset.variantId = String(variant.id);
      this._syncBasePills(card, variant.id);

      this.state.base = { product: product, variant: variant };
      if (this.$next) this.$next.disabled = false;
      this._applyBaseImage();
      this._renderRecap();
      this._renderTotal();
      if (!silent) {
        this._hideMessages();
        this._save();
        haptic(8);
      }
    }

    _syncBasePills(card, variantId) {
      card.querySelectorAll('[data-ab-variant]').forEach(function (p) {
        var on = parseInt(p.dataset.abVariant, 10) === parseInt(variantId, 10);
        p.classList.toggle('is-active', on);
        p.setAttribute('aria-checked', on ? 'true' : 'false');
      });
    }

    _selectBaseVariant(card, variantId) {
      var product = parseProduct(card);
      var v = findVariant(product, variantId);
      if (!v || !v.available) return;
      card.dataset.variantId = String(v.id);
      this._syncBasePills(card, v.id);
      if (!card.classList.contains('is-active')) {
        this._selectBase(card, false);
        return;
      }
      this.state.base = { product: product, variant: v };
      this._applyBaseImage();
      this._renderRecap();
      this._renderTotal();
      this._save();
    }

    _baseImage() {
      if (!this.state.base) return '';
      var v = this.state.base.variant;
      return (v && v.image) || this.state.base.product.image || '';
    }

    _applyBaseImage() {
      if (!this.$canvasBase) return;
      var img = this._baseImage();
      if (img) {
        if (this._baseSrcWanted !== img) {
          this._baseSrcWanted = img;
          this.$canvasBase.src = img;
        }
        this.$canvasBase.alt = this.state.base.product.title || '';
      } else {
        this._baseSrcWanted = '';
        this.$canvasBase.removeAttribute('src');
        this.$canvasBase.alt = '';
      }
      this._frameBase(img);
    }

    /* Cadrage automatique : la photo reste masquee le temps de l'analyse
       (image en cache le plus souvent), puis elle est placee pour que le
       bijou occupe toujours la meme zone du canvas. Echec (photo sans
       fond detectable, image inaccessible, plus de 3 s) : photo entiere,
       en haut du canvas. */
    _frameBase(img) {
      var self = this;
      var token = ++this._frameToken;
      if (!this._auto || !img) {
        this._frame = null;
        this._framing = false;
        this._applyFrame(null);
        return;
      }
      if (this._frameSrc === img && this._frame) {
        this._applyFrame(this._frame);
        return;
      }
      this._frame = null;
      this._frameSrc = img;
      this._framing = true;
      this._applyFrame(null);
      if (this.$canvas) this.$canvas.classList.add('is-framing');
      var fallback = setTimeout(function () {
        if (token !== self._frameToken) return;
        if (self.$canvas) self.$canvas.classList.remove('is-framing');
        if (self._fsOpen) self._fillFullscreen();
      }, 3000);
      detectJewel(img).then(function (res) {
        if (token !== self._frameToken) return;
        clearTimeout(fallback);
        self._framing = false;
        self._frame = computeFrame(res, self.cfg.autoFrame);
        self._applyFrame(self._frame);
        if (self.$canvas) self.$canvas.classList.remove('is-framing');
        self._afterFrame();
      });
    }

    _applyFrame(frame) {
      var el = this.$canvasBase;
      if (!el) return;
      /* Photo non detouree sur fond clair : son fond se fond dans le canvas */
      var blend = !!(frame && frame.lift);
      el.classList.toggle('is-blend', blend);
      el.style.filter = blend && frame.lift > 1 ? 'brightness(' + frame.lift + ')' : '';
      if (frame) {
        el.classList.add('is-framed');
        el.style.left = frame.l + '%';
        el.style.top = frame.t + '%';
        el.style.width = frame.w + '%';
        el.style.height = frame.h + '%';
        this._sharpen(frame);
      } else {
        el.classList.remove('is-framed');
        el.style.left = '';
        el.style.top = '';
        el.style.width = '';
        el.style.height = '';
        if (this._baseSrcWanted && el.getAttribute('src') !== this._baseSrcWanted) el.src = this._baseSrcWanted;
      }
    }

    /* Bijou petit sur sa photo : la photo est agrandie au-dela du canvas.
       On demande au CDN une largeur a la mesure de l'agrandissement (le
       CDN ne depasse jamais la taille d'origine). */
    _sharpen(frame) {
      var el = this.$canvasBase;
      var want = this._baseSrcWanted;
      if (!want || frame.w <= 100 || !/[?&]width=\d+/.test(want)) return;
      var cw = (this.$canvas && this.$canvas.clientWidth) || parseInt(this.cfg.canvasMax, 10) || 520;
      var dpr = Math.min(2, window.devicePixelRatio || 1);
      var need = Math.min(2400, Math.ceil(cw * dpr * frame.w / 100 / 100) * 100);
      var cur = parseInt((want.match(/[?&]width=(\d+)/) || [])[1], 10) || 0;
      if (need <= cur * 1.15) return;
      var hi = want.replace(/([?&]width=)\d+/, function (m, p1) { return p1 + need; });
      if (el.getAttribute('src') !== hi) el.src = hi;
    }

    /* Cadrage connu : charms restaures a reprojeter, charms ajoutes pendant
       l'analyse a accrocher, recapitulatif et plein ecran a rafraichir. */
    _afterFrame() {
      var changed = this._afterFrameRestore();
      if (this._rehangPending()) changed = true;
      if (this.state.charms.length) {
        if (changed) {
          this._renderPlaced();
          this._save();
        }
        this._renderRecap();
        this._renderOrder();
      }
      if (this._fsOpen) this._fillFullscreen();
    }

    /* Charms restaures depuis le stockage : positions sauvees avec le
       cadrage de l'epoque. Si le bijou a bouge (autre photo, autres
       reglages), les charms suivent ; sans cadrage sauve, ils sont
       raccroches. Attend que le cadrage courant soit connu. */
    _afterFrameRestore() {
      var restored = this.state.charms.filter(function (c) { return c._restored; });
      if (!restored.length) return false;
      if (!this._auto) {
        restored.forEach(function (c) { delete c._restored; });
        this._restoredFrame = null;
        return false;
      }
      if (!this._frame) return false;
      var self = this;
      var from = this._restoredFrame;
      var to = this._frame;
      var changed = false;
      restored.forEach(function (c) {
        delete c._restored;
        if (from) {
          c.x = round2(clamp(to.l + (c.x - from.l) * to.w / from.w, 2, 98));
          c.y = round2(clamp(to.t + (c.y - from.t) * to.h / from.h, 2, 98));
          changed = true;
        } else if (self._hang) {
          c._hangLater = { x: c.x, y: c.y };
        }
      });
      /* Charms encore en attente (onglet charge plus tard) : meme cadrage d'origine */
      if (!this._pendingCharms) this._restoredFrame = null;
      return changed;
    }

    /* Charms poses avant la fin de l'analyse (ou restaures sans cadrage) :
       accroches maintenant s'ils n'ont pas ete deplaces. */
    _rehangPending() {
      if (!this._hang || !this._frame) return false;
      var self = this;
      var moved = false;
      var list = this.state.charms.filter(function (c) { return c._hangLater; });
      list.forEach(function (c) {
        var at = c._hangLater;
        delete c._hangLater;
        if (c.x !== at.x || c.y !== at.y) return;
        var others = self.state.charms.filter(function (o) { return o !== c && !o._hangLater; });
        var slot = self._hangSlot(c.s, BOUNDS_CACHE[self._charmImage(c)] || null, others);
        if (!slot) return;
        self._patchHistory(c.id, at, slot.pos);
        c.x = slot.pos.x;
        c.y = slot.pos.y;
        moved = true;
      });
      return moved;
    }

    /* Met a jour une position provisoire dans l'historique d'annulation */
    _patchHistory(id, from, to) {
      this.history = this.history.map(function (snap) {
        try {
          var arr = JSON.parse(snap);
          var hit = false;
          arr.forEach(function (c) {
            if (c.id === id && c.x === from.x && c.y === from.y) { c.x = to.x; c.y = to.y; hit = true; }
          });
          return hit ? JSON.stringify(arr) : snap;
        } catch (e) {
          return snap;
        }
      });
    }

    /* ---------------------------------------------------------- */
    /* Navigation                                                  */
    /* ---------------------------------------------------------- */
    _goToStep(target) {
      if (target === 2 && !this.state.base) {
        this._showError(this.cfg.msg.chooseBase || 'Choisissez votre base avant de continuer.');
        return;
      }
      this._hideMessages();
      this.step = target;
      this.$panes.forEach(function (p) {
        p.classList.toggle('is-active', parseInt(p.dataset.pane, 10) === target);
      });
      var self = this;
      this.$steps.forEach(function (s) {
        var n = parseInt(s.dataset.abStep, 10);
        s.classList.remove('is-active', 'is-done');
        s.removeAttribute('aria-current');
        if (n < self.step) s.classList.add('is-done');
        if (n === self.step) {
          s.classList.add('is-active');
          s.setAttribute('aria-current', 'step');
        }
      });
      if (this.$fill) this.$fill.style.width = ((this.step - 1) / Math.max(1, this.$steps.length - 1)) * 100 + '%';

      if (target === 2) {
        this._applyBaseImage();
        this._applyPendingCharms(true);
        this._renderAll();
        var active = this.querySelector('[data-ab-grid].is-active');
        if (active) this._ensureGridLoaded(active);
        /* Composition restauree avec des charms dans un onglet non charge :
           on charge tous les onglets incomplets sans attendre un clic. */
        if (this._pendingCharms && this._pendingCharms.length) {
          var self2 = this;
          this.$grids.forEach(function (g) { self2._ensureGridLoaded(g); });
        }
      }
      this._scrollToTop();
    }

    _scrollToTop() {
      var header = 0;
      try {
        header = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height'), 10) || 0;
      } catch (e) { header = 0; }
      var top = this.getBoundingClientRect().top + window.pageYOffset - header - 16;
      var reduced = false;
      try { reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { reduced = false; }
      try {
        window.scrollTo({ top: Math.max(0, top), behavior: reduced ? 'auto' : 'smooth' });
      } catch (e) {
        window.scrollTo(0, Math.max(0, top));
      }
    }

    /* ---------------------------------------------------------- */
    /* Onglets, recherche, chargement au-dela de 50 produits        */
    /* ---------------------------------------------------------- */
    _activateTab(id, focusTab) {
      var self = this;
      this.$tabs.forEach(function (t) {
        var on = t.dataset.abTab === id;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.setAttribute('tabindex', on ? '0' : '-1');
        if (on && focusTab) t.focus();
      });
      this.$grids.forEach(function (g) {
        var on = g.dataset.abGrid === id;
        g.classList.toggle('is-active', on);
        g.hidden = !on;
        if (on) self._ensureGridLoaded(g);
      });
      if (this.$search && this.$search.value) {
        this.$search.value = '';
      }
      this._filterCharms('');
    }

    _filterCharms(query) {
      query = (query || '').trim().toLowerCase();
      var grid = this.querySelector('[data-ab-grid].is-active');
      if (!grid) return;
      var visible = 0;
      grid.querySelectorAll('[data-ab-charm]').forEach(function (card) {
        var match = !query || (card.dataset.name || '').indexOf(query) !== -1;
        card.classList.toggle('is-hidden', !match);
        if (match) visible++;
      });
      if (this.$searchEmpty) this.$searchEmpty.hidden = visible > 0;
    }

    _ensureGridLoaded(grid) {
      var total = parseInt(grid.dataset.total, 10) || 0;
      var rendered = parseInt(grid.dataset.rendered, 10) || 0;
      var handle = grid.dataset.handle;
      if (!handle || total <= rendered || this._loadedGrids[handle]) return;
      this._loadedGrids[handle] = true;
      var self = this;
      var more = document.createElement('p');
      more.className = 'cds-ab__loading-more';
      more.textContent = '…';
      grid.appendChild(more);

      fetch('/collections/' + encodeURIComponent(handle) + '/products.json?limit=250')
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
        .then(function (data) {
          var present = {};
          grid.querySelectorAll('[data-ab-charm]').forEach(function (c) { present[c.dataset.productId] = true; });
          (data.products || []).forEach(function (p) {
            if (present[String(p.id)]) return;
            grid.insertBefore(self._buildCharmCard(p), more);
          });
          more.remove();
          grid.dataset.rendered = String(grid.querySelectorAll('[data-ab-charm]').length);
          self._applyPendingCharms();
          self._updateCharmCounts();
          if (self.$search && self.$search.value) self._filterCharms(self.$search.value);
        })
        .catch(function () {
          more.remove();
          self._loadedGrids[handle] = false;
          self._showError(self.cfg.msg.loadError || 'Certains charms n’ont pas pu être chargés.');
        });
    }

    /* Carte charm construite depuis /collections/x/products.json (meme
       markup que la carte Liquid, valeurs echappees via textContent). */
    _buildCharmCard(p) {
      var self = this;
      var variants = (p.variants || []).map(function (v) {
        return {
          id: v.id,
          title: v.title || '',
          price: Math.round(parseFloat(v.price) * 100) || 0,
          available: !!v.available,
          image: v.featured_image && v.featured_image.src ? sizedImg(v.featured_image.src, '600x') : null
        };
      });
      var available = variants.some(function (v) { return v.available; });
      var img = (p.images && p.images[0] && p.images[0].src) ? p.images[0].src : '';
      var minPrice = variants.length ? Math.min.apply(null, variants.map(function (v) { return v.price; })) : 0;
      var first = variants.filter(function (v) { return v.available; })[0] || variants[0] || { id: 0 };

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'cds-ab__charm' + (available ? '' : ' is-out-of-stock');
      btn.setAttribute('data-ab-charm', '');
      btn.dataset.productId = String(p.id);
      btn.dataset.handle = p.handle || '';
      btn.dataset.title = p.title || '';
      btn.dataset.name = (p.title || '').toLowerCase();
      btn.dataset.price = String(minPrice);
      btn.dataset.available = available ? 'true' : 'false';
      btn.dataset.multi = variants.length > 1 ? 'true' : 'false';
      btn.dataset.variantId = String(first.id);
      btn.dataset.image = img ? sizedImg(img, '600x') : '';
      btn.dataset.variants = JSON.stringify(variants);
      if (!available) btn.setAttribute('aria-disabled', 'true');

      var count = document.createElement('span');
      count.className = 'cds-ab__charm-count';
      count.setAttribute('data-ab-count', String(p.id));
      count.hidden = true;
      btn.appendChild(count);

      var wrap = document.createElement('span');
      wrap.className = 'cds-ab__charm-img';
      if (img) {
        var im = document.createElement('img');
        im.src = sizedImg(img, '240x');
        im.alt = p.title || '';
        im.loading = 'lazy';
        im.width = 120;
        im.height = 120;
        wrap.appendChild(im);
      }
      if (!available) {
        var badge = document.createElement('span');
        badge.className = 'cds-ab__stock-badge';
        badge.textContent = self.cfg.msg.soldOut || 'Épuisé';
        wrap.appendChild(badge);
      }
      btn.appendChild(wrap);

      var name = document.createElement('span');
      name.className = 'cds-ab__charm-name';
      name.textContent = p.title || '';
      btn.appendChild(name);

      if (minPrice > 0) {
        var price = document.createElement('span');
        price.className = 'cds-ab__charm-price';
        price.textContent = self._money(minPrice);
        btn.appendChild(price);
      }
      return btn;
    }

    /* ---------------------------------------------------------- */
    /* Etape 2 : charms                                            */
    /* ---------------------------------------------------------- */
    _onCharmClick(card) {
      if (card.classList.contains('is-out-of-stock')) return;
      if (this.state.charms.length >= this.maxCharms) {
        this._showError(this.cfg.msg.maxCharms || 'Maximum atteint.');
        haptic([20, 40, 20]);
        return;
      }
      var product = parseProduct(card);
      if (!product) return;
      var grid = card.closest('[data-ab-grid]');
      var size = sizeToCanvasPct(grid ? grid.dataset.charmSize : 70);
      if (product.multi) {
        this._openVariantModal(product, size, card);
        return;
      }
      var v = firstAvailable(product, product.variantId);
      if (!v) return;
      this._addCharm(product, v, size);
    }

    _smartPosition(n) {
      if (n === 0) return { x: 50, y: 56 };
      var radius = Math.min(24, 8 + n * 2);
      var angle = ((n - 1) * (360 / 7)) * (Math.PI / 180);
      return { x: round2(50 + radius * Math.cos(angle)), y: round2(56 + radius * Math.sin(angle) * 0.6) };
    }

    /* Bas du profil de la piece a l'abscisse x (le bas d'un anneau
       remonte vers ses cotes). null hors de la piece. */
    _profileY(piece, x) {
      var pr = piece.profile;
      if (!pr.length || x < pr[0][0] - 0.5 || x > pr[pr.length - 1][0] + 0.5) return null;
      var best = null, dist = Infinity;
      for (var i = 0; i < pr.length; i++) {
        var dd = Math.abs(pr[i][0] - x);
        if (dd < dist) { dist = dd; best = pr[i]; }
      }
      return best ? best[1] : null;
    }

    /* Largeur visible d'un charm (en % du canvas) */
    _visibleWidth(size, bounds) {
      return bounds ? size * bounds.w / 100 : size * 0.5;
    }

    /* Centre d'un charm accroche sous une piece, emplacement o (0 : au
       milieu, -1 / +1 : de part et d'autre, ...). Le haut du charm visible
       (son anneau) chevauche legerement le bas de la piece. null si
       l'emplacement sort de la piece ou remonte trop sur son flanc. */
    _hangAt(slot, size, bounds) {
      var piece = slot.piece;
      var gap = Math.max(2, this._visibleWidth(size, bounds) * (slot.f || 1.05));
      var tx = piece.x + slot.o * gap;
      var py = this._profileY(piece, tx);
      if (py === null) return null;
      var radius = Math.max(1, (piece.y - piece.top) / 2);
      if (slot.o !== 0 && py < piece.y - 0.35 * radius) return null;
      var ar = (bounds && bounds.ar) || 1;
      var topIn = bounds ? bounds.y / 100 : 0.06;
      var midIn = bounds ? (bounds.x + bounds.w / 2) / 100 : 0.5;
      var x = tx - (midIn - 0.5) * size + (slot.dx || 0);
      var y = py + (0.5 - topIn) * size * ar - 0.8;
      return { x: round2(clamp(x, 2, 98)), y: round2(clamp(y, 2, 98)) };
    }

    _pieceIndex(x) {
      var f = this._frame;
      if (!f || f.pieces.length < 2) return 0;
      var split = f.split !== null && f.split !== undefined ? f.split : (f.pieces[0].x + f.pieces[1].x) / 2;
      var leftIdx = f.pieces[0].x <= f.pieces[1].x ? 0 : 1;
      return x < split ? leftIdx : 1 - leftIdx;
    }

    /* Premier emplacement libre : milieu de chaque piece (anneau gauche,
       puis droit), puis de part et d'autre, en restant sur le bas de la
       piece ; second passage avec des charms qui se chevauchent a moitie
       (charms larges sur un petit anneau). Tout est pris : milieu de la
       piece la moins chargee, legerement decale pour rester visible. */
    _hangSlot(size, bounds, others) {
      var self = this;
      var pieces = (this._frame && this._frame.pieces) || [];
      if (!pieces.length) return null;
      var charms = others || this.state.charms;
      var visW = this._visibleWidth(size, bounds);
      var offsets = [0, -1, 1, -2, 2, -3, 3];
      var passes = [1.05, 0.55];
      for (var pi = 0; pi < passes.length; pi++) {
        var near = passes[pi] * 0.75;
        for (var j = 0; j < offsets.length; j++) {
          for (var k = 0; k < pieces.length; k++) {
            var slot = { piece: pieces[k], o: offsets[j], f: passes[pi] };
            var pos = this._hangAt(slot, size, bounds);
            if (!pos) continue;
            var taken = charms.some(function (c) {
              var cw = self._visibleWidth(c.s, BOUNDS_CACHE[self._charmImage(c)] || null);
              return Math.abs(c.x - pos.x) < Math.max(visW, cw) * near && Math.abs(c.y - pos.y) < size * 0.45;
            });
            if (!taken) return { slot: slot, pos: pos };
          }
        }
      }
      var load = pieces.map(function () { return 0; });
      charms.forEach(function (c) { load[self._pieceIndex(c.x)]++; });
      var pick = load.indexOf(Math.min.apply(null, load));
      var s0 = { piece: pieces[pick], o: 0, dx: (load[pick] % 2 ? -1 : 1) * Math.min(6, 2 * load[pick]) };
      var p0 = this._hangAt(s0, size, bounds);
      return p0 ? { slot: s0, pos: p0 } : null;
    }

    _addCharm(product, variant, size) {
      var self = this;
      this._pushHistory();
      var src = (variant && variant.image) || product.image || '';
      var known = Object.prototype.hasOwnProperty.call(BOUNDS_CACHE, src);
      var hang = this._hang && this._frame ? this._hangSlot(size, known ? BOUNDS_CACHE[src] : null) : null;
      var pos = hang ? hang.pos : this._smartPosition(this.state.charms.length);
      var id = uid();
      var charm = {
        id: id,
        product: { id: product.id, handle: product.handle, title: product.title, image: product.image, multi: product.multi },
        variant: { id: variant.id, title: variant.title || '', price: variant.price || 0, image: variant.image || null },
        x: pos.x,
        y: pos.y,
        s: size
      };
      /* Analyse de la photo pas encore terminee : accroche des qu'elle l'est */
      if (this._hang && !this._frame && this._framing) charm._hangLater = { x: pos.x, y: pos.y };
      this.state.charms.push(charm);
      this._lastAdded = id;
      this._hideMessages();
      this._renderAll(product.id);
      this._save();
      this._maybeShowHint();
      haptic(12);

      /* Premier ajout de ce charm : sa zone visible n'est pas encore
         connue, on affine l'accroche des qu'elle l'est (sauf si le charm
         a ete deplace entre-temps). L'historique suit. */
      if (hang && !known && src) {
        computeImageBounds(src).then(function (b) {
          if (!b) return;
          var c = null;
          self.state.charms.forEach(function (it) { if (it.id === id) c = it; });
          if (!c || c.x !== pos.x || c.y !== pos.y) return;
          var p2 = self._hangAt(hang.slot, size, b);
          if (!p2) return;
          self._patchHistory(id, pos, p2);
          c.x = p2.x;
          c.y = p2.y;
          var el = self.$layer ? self.$layer.querySelector('[data-charm-id="' + id + '"]') : null;
          if (el) {
            el.style.left = c.x + '%';
            el.style.top = c.y + '%';
          }
          self._renderOrder();
          self._renderRecap();
          self._save();
        });
      }
    }

    _removeCharm(id) {
      var removed = null;
      this.state.charms.forEach(function (c) { if (c.id === id) removed = c; });
      if (!removed) return;
      this._pushHistory();
      this.state.charms = this.state.charms.filter(function (c) { return c.id !== id; });
      this._renderAll(removed.product.id);
      this._save();
      haptic(8);
    }

    _charmImage(c) {
      return (c.variant && c.variant.image) || (c.product && c.product.image) || '';
    }

    /* Paire de creoles detectee : 0 anneau gauche, 1 anneau droit (le
       plus proche du charm). -1 hors paire. */
    _isPair() {
      return !!(this._frame && this._frame.pieces && this._frame.pieces.length === 2);
    }

    _sideOf(c) {
      if (!this._isPair()) return -1;
      var p = this._frame.pieces;
      var leftIdx = p[0].x <= p[1].x ? 0 : 1;
      return this._pieceIndex(c.x) === leftIdx ? 0 : 1;
    }

    _sideLabel(side) {
      var m = this.cfg.msg;
      if (side === 0) return m.sideLeft || 'Anneau gauche';
      if (side === 1) return m.sideRight || 'Anneau droit';
      return '';
    }

    _ordered() {
      var self = this;
      var pair = this._isPair();
      return this.state.charms.slice().sort(function (a, b) {
        if (pair) {
          var sa = self._sideOf(a), sb = self._sideOf(b);
          if (sa !== sb) return sa - sb;
        }
        if (a.x !== b.x) return a.x - b.x;
        return a.y - b.y;
      });
    }

    _renderAll(productId) {
      this._renderPlaced();
      this._updateCanvasInfo();
      this._updateCharmCounts(productId);
      this._renderRecap();
      this._renderOrder();
      this._renderTotal();
    }

    _renderPlaced() {
      var self = this;
      if (!this.$layer) return;
      this.$layer.innerHTML = '';
      var has = this.state.charms.length > 0;
      if (this.$instruction) this.$instruction.classList.toggle('is-hidden', has);

      this.state.charms.forEach(function (charm, idx) {
        var el = document.createElement('div');
        el.className = 'cds-ab__placed';
        el.style.left = charm.x + '%';
        el.style.top = charm.y + '%';
        el.style.width = charm.s + '%';
        el.dataset.charmId = charm.id;
        el.setAttribute('tabindex', '0');
        el.setAttribute('role', 'button');
        el.setAttribute('aria-label', charm.product.title + ' – flèches pour déplacer, Suppr pour retirer');

        var src = self._charmImage(charm);
        if (src) {
          var img = document.createElement('img');
          img.src = src;
          img.alt = '';
          img.draggable = false;
          el.appendChild(img);
        }

        var bounds = document.createElement('div');
        bounds.className = 'cds-ab__placed-bounds';
        el.appendChild(bounds);

        var rm = document.createElement('button');
        rm.type = 'button';
        rm.className = 'cds-ab__placed-remove';
        rm.setAttribute('aria-label', (self.cfg.msg.removeAria || 'Retirer') + ' ' + charm.product.title);
        rm.innerHTML = ICON_CLOSE;
        rm.addEventListener('pointerdown', function (e) { e.stopPropagation(); });
        rm.addEventListener('click', function (e) {
          e.stopPropagation();
          self._removeCharm(charm.id);
        });
        bounds.appendChild(rm);

        self._attachPointer(el, bounds, charm);
        self._attachKeyboard(el, charm);
        self.$layer.appendChild(el);

        if (charm.id === self._lastAdded) {
          self._lastAdded = null;
          requestAnimationFrame(function () {
            el.classList.add('is-selected');
            setTimeout(function () { el.classList.remove('is-selected'); }, 3000);
          });
        }

        if (src) {
          computeImageBounds(src).then(function (b) {
            if (!b || !bounds.isConnected) return;
            bounds.style.left = b.x + '%';
            bounds.style.top = b.y + '%';
            bounds.style.width = b.w + '%';
            bounds.style.height = b.h + '%';
          });
        }
      });
    }

    _attachPointer(el, bounds, charm) {
      var self = this;
      var canvas = this.$canvas;
      var dragging = false;
      var moved = false;
      var startX = 0, startY = 0, startCX = 0, startCY = 0, rect = null, before = null;

      var live = rafThrottle(function () {
        el.style.left = charm.x + '%';
        el.style.top = charm.y + '%';
        self._renderOrder();
        self._renderRecap();
      });

      function end(e) {
        if (!dragging) return;
        dragging = false;
        self._dragging = false;
        try { bounds.releasePointerCapture(e.pointerId); } catch (err) { /* deja libere */ }
        el.classList.remove('is-dragging');
        if (moved) {
          self._pushSnapshot(before);
          self._save();
          haptic(6);
        }
        setTimeout(function () { el.classList.remove('is-selected'); }, 3000);
      }

      bounds.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        if (e.target.closest('.cds-ab__placed-remove')) return;
        e.preventDefault();
        dragging = true;
        moved = false;
        before = self._snapshot();
        rect = canvas.getBoundingClientRect();
        startX = e.clientX;
        startY = e.clientY;
        startCX = charm.x;
        startCY = charm.y;
        el.classList.add('is-dragging', 'is-selected');
        self._dragging = true;
        try { bounds.setPointerCapture(e.pointerId); } catch (err) { /* navigateur ancien */ }
      });
      bounds.addEventListener('pointermove', function (e) {
        if (!dragging || !rect) return;
        e.preventDefault();
        var dx = e.clientX - startX;
        var dy = e.clientY - startY;
        if (!moved && (Math.abs(dx) > 2 || Math.abs(dy) > 2)) moved = true;
        charm.x = round2(clamp(startCX + (dx / rect.width) * 100, 2, 98));
        charm.y = round2(clamp(startCY + (dy / rect.height) * 100, 2, 98));
        live();
      });
      bounds.addEventListener('pointerup', end);
      bounds.addEventListener('pointercancel', end);
      bounds.addEventListener('lostpointercapture', end);
    }

    _attachKeyboard(el, charm) {
      var self = this;
      el.addEventListener('keydown', function (e) {
        var step = e.shiftKey ? 5 : 1;
        var dx = 0, dy = 0;
        switch (e.key) {
          case 'ArrowLeft': dx = -step; break;
          case 'ArrowRight': dx = step; break;
          case 'ArrowUp': dy = -step; break;
          case 'ArrowDown': dy = step; break;
          case 'Delete':
          case 'Backspace':
            e.preventDefault();
            self._removeCharm(charm.id);
            return;
          default:
            return;
        }
        e.preventDefault();
        if (!self._kbTimer) self._pushHistory();
        clearTimeout(self._kbTimer);
        self._kbTimer = setTimeout(function () { self._kbTimer = null; }, 800);
        charm.x = round2(clamp(charm.x + dx, 2, 98));
        charm.y = round2(clamp(charm.y + dy, 2, 98));
        el.style.left = charm.x + '%';
        el.style.top = charm.y + '%';
        self._renderOrder();
        self._renderRecap();
        self._save();
      });
    }

    _updateFloat() {
      if (!this.$float) return;
      var mobile = window.innerWidth < 992;
      var show = mobile && this.step === 2 && this.state.charms.length > 0 && !this._canvasVisible;
      this.$float.hidden = !show;
      if (this.$floatCount) this.$floatCount.textContent = String(this.state.charms.length);
    }

    _maybeShowHint() {
      if (!this.$hint) return;
      try { if (localStorage.getItem(HINT_KEY)) return; } catch (e) { /* prive */ }
      if (this.state.charms.length !== 1) return;
      if (!this._canvasVisible) return;
      this.$hint.classList.add('is-visible');
      var self = this;
      setTimeout(function () {
        self.$hint.classList.remove('is-visible');
        try { localStorage.setItem(HINT_KEY, '1'); } catch (e) { /* prive */ }
      }, 3500);
    }

    _updateCharmCounts(productId) {
      var counts = {};
      this.state.charms.forEach(function (c) {
        counts[c.product.id] = (counts[c.product.id] || 0) + 1;
      });
      this.querySelectorAll('[data-ab-count]').forEach(function (badge) {
        var n = counts[badge.dataset.abCount] || 0;
        if (n > 0) {
          badge.textContent = 'x' + n;
          badge.hidden = false;
          if (productId && String(productId) === badge.dataset.abCount) {
            badge.classList.remove('is-pop');
            void badge.offsetWidth;
            badge.classList.add('is-pop');
          }
        } else {
          badge.hidden = true;
          badge.textContent = '';
        }
      });
      this.section.classList.toggle('is-limit', this.state.charms.length >= this.maxCharms);
    }

    _updateCanvasInfo() {
      var n = this.state.charms.length;
      var m = this.cfg.msg;
      if (this.$charmsCount) this.$charmsCount.textContent = n + ' / ' + this.maxCharms + ' ' + (this.maxCharms > 1 ? (m.charmMany || 'charms') : (m.charmOne || 'charm'));
      this._updateFloat();
      if (this.$baseSummary) this.$baseSummary.textContent = this.state.base ? this.state.base.product.title : ' ';
    }

    /* ---------------------------------------------------------- */
    /* Recapitulatif, ordre, total                                 */
    /* ---------------------------------------------------------- */
    _totalCents() {
      var total = this.parentPrice;
      if (this.state.base && this.state.base.variant) total += this.state.base.variant.price || 0;
      this.state.charms.forEach(function (c) { total += c.variant.price || 0; });
      return total;
    }

    _renderTotal(instant) {
      var total = this._totalCents();
      if (this.$totalBtn) this.$totalBtn.textContent = this._money(total);
      if (this.$total) {
        var reduced = false;
        try { reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { reduced = false; }
        if (instant || reduced || this._dragging || this._lastTotal === total) {
          this.$total.textContent = this._money(total);
        } else {
          this._animateTotal(this._lastTotal, total);
        }
      }
      this._lastTotal = total;
      this._renderBreakdown();
    }

    _animateTotal(from, to) {
      var self = this;
      var start = performance.now();
      var dur = 320;
      if (this._totalRaf) cancelAnimationFrame(this._totalRaf);
      function tick(now) {
        var t = Math.min(1, (now - start) / dur);
        var ease = 1 - Math.pow(1 - t, 3);
        self.$total.textContent = self._money(Math.round(from + (to - from) * ease));
        if (t < 1) {
          self._totalRaf = requestAnimationFrame(tick);
        } else {
          self.$total.classList.remove('is-pulsing');
          void self.$total.offsetWidth;
          self.$total.classList.add('is-pulsing');
        }
      }
      this._totalRaf = requestAnimationFrame(tick);
    }

    _renderBreakdown() {
      if (!this.$breakdown) return;
      var parts = [];
      var m = this.cfg.msg;
      if (this.parentPrice > 0) parts.push((m.baseItem || 'Bijou de base') + ' ' + this._money(this.parentPrice));
      if (this.state.base && this.state.base.variant && this.state.base.variant.price > 0) {
        parts.push((this.cfg.baseLabel || 'Base') + ' ' + this._money(this.state.base.variant.price));
      }
      var charmsTotal = 0;
      this.state.charms.forEach(function (c) { charmsTotal += c.variant.price || 0; });
      if (this.state.charms.length && charmsTotal > 0) {
        var n = this.state.charms.length;
        parts.push(n + ' ' + (n > 1 ? (m.charmMany || 'charms') : (m.charmOne || 'charm')) + ' ' + this._money(charmsTotal));
      }
      this.$breakdown.textContent = parts.join(' · ');
      this.$breakdown.hidden = parts.length === 0;
    }

    _recapItem(d) {
      var self = this;
      var el = document.createElement('div');
      el.className = 'cds-ab__recap-item cds-ab__recap-item--' + d.type;

      var imgWrap = document.createElement('div');
      imgWrap.className = 'cds-ab__recap-img';
      if (d.img) {
        var im = document.createElement('img');
        im.src = d.img;
        im.alt = '';
        imgWrap.appendChild(im);
      }
      el.appendChild(imgWrap);

      var info = document.createElement('div');
      info.className = 'cds-ab__recap-info';
      var name = document.createElement('div');
      name.className = 'cds-ab__recap-name';
      name.textContent = d.title;
      var meta = document.createElement('div');
      meta.className = 'cds-ab__recap-meta';
      meta.textContent = d.meta;
      info.appendChild(name);
      info.appendChild(meta);
      el.appendChild(info);

      var price = document.createElement('div');
      price.className = 'cds-ab__recap-price';
      if (d.price > 0) {
        price.textContent = this._money(d.price);
      } else {
        price.classList.add('cds-ab__recap-price--included');
        price.textContent = this.cfg.msg.included || 'Inclus';
      }
      el.appendChild(price);

      if (d.type === 'charm' && d.charmId) {
        var rm = document.createElement('button');
        rm.type = 'button';
        rm.className = 'cds-ab__recap-remove';
        rm.setAttribute('aria-label', (this.cfg.msg.removeAria || 'Retirer') + ' ' + d.title);
        rm.innerHTML = ICON_CLOSE;
        rm.addEventListener('click', function () { self._removeCharm(d.charmId); });
        el.appendChild(rm);
      }
      return el;
    }

    _renderRecap() {
      if (!this.$recapList) return;
      var self = this;
      var m = this.cfg.msg;
      this.$recapList.innerHTML = '';
      var count = 0;

      if (this.cfg.parent && this.cfg.parent.title) {
        count++;
        this.$recapList.appendChild(this._recapItem({
          type: 'parent',
          title: this.cfg.parent.title,
          meta: m.baseItem || 'Bijou de base',
          price: this.parentPrice,
          img: this.cfg.parent.image || ''
        }));
      }
      if (this.state.base) {
        count++;
        var b = this.state.base;
        this.$recapList.appendChild(this._recapItem({
          type: 'base',
          title: b.product.title + (b.product.multi && b.variant.title ? ' (' + b.variant.title + ')' : ''),
          meta: this.cfg.baseLabel || 'Base',
          price: b.variant.price || 0,
          img: this._baseImage()
        }));
      }
      this._ordered().forEach(function (c, i) {
        count++;
        self.$recapList.appendChild(self._recapItem({
          type: 'charm',
          title: c.product.title + (c.product.multi && c.variant.title ? ' (' + c.variant.title + ')' : ''),
          meta: (m.charmOne ? m.charmOne.charAt(0).toUpperCase() + m.charmOne.slice(1) : 'Charm') + ' ' + (i + 1) + (self._sideOf(c) >= 0 ? ' · ' + self._sideLabel(self._sideOf(c)) : ''),
          price: c.variant.price || 0,
          img: self._charmImage(c),
          charmId: c.id
        }));
      });

      if (this.$detailsCount) {
        this.$detailsCount.textContent = count ? '(' + count + ' ' + (count > 1 ? (m.articleMany || 'articles') : (m.articleOne || 'article')) + ')' : '';
      }
      if (this.$details && this.state.charms.length > 0 && !this.$details.dataset.userToggled) {
        this.$details.open = true;
      }
    }

    _renderOrder() {
      if (!this.$order || !this.$orderRow) return;
      var ordered = this._ordered();
      if (!ordered.length) {
        this.$order.hidden = true;
        this.$orderRow.innerHTML = '';
        return;
      }
      this.$order.hidden = false;
      this.$orderRow.innerHTML = '';
      var self = this;
      var prevSide = null;
      ordered.forEach(function (c, i) {
        var side = self._sideOf(c);
        if (prevSide !== null && side !== prevSide) {
          var sep = document.createElement('span');
          sep.className = 'cds-ab__order-sep';
          sep.setAttribute('aria-hidden', 'true');
          self.$orderRow.appendChild(sep);
        }
        prevSide = side;
        var slot = document.createElement('div');
        slot.className = 'cds-ab__order-slot';
        if (side >= 0) slot.title = self._sideLabel(side);
        var imgWrap = document.createElement('div');
        imgWrap.className = 'cds-ab__order-img';
        var src = self._charmImage(c);
        if (src) {
          var im = document.createElement('img');
          im.src = src;
          im.alt = '';
          imgWrap.appendChild(im);
        }
        slot.appendChild(imgWrap);
        var num = document.createElement('span');
        num.className = 'cds-ab__order-num';
        num.textContent = String(i + 1);
        slot.appendChild(num);
        var label = document.createElement('span');
        label.className = 'cds-ab__order-label';
        label.textContent = c.product.title;
        label.title = c.product.title;
        slot.appendChild(label);
        self.$orderRow.appendChild(slot);
      });
    }

    _updateNoteCounter() {
      if (!this.$noteCounter || !this.$note) return;
      var max = parseInt(this.cfg.noteMax, 10) || parseInt(this.$note.getAttribute('maxlength'), 10) || 500;
      this.$noteCounter.textContent = (this.$note.value || '').length + '/' + max;
    }

    /* ---------------------------------------------------------- */
    /* Undo                                                        */
    /* ---------------------------------------------------------- */
    _snapshot() {
      return JSON.stringify(this.state.charms.map(function (c) {
        return { id: c.id, product: c.product, variant: c.variant, x: c.x, y: c.y, s: c.s };
      }));
    }

    _pushSnapshot(snap) {
      this.history.push(snap);
      if (this.history.length > HISTORY_MAX) this.history.shift();
      this._refreshUndo();
    }

    _pushHistory() {
      this._pushSnapshot(this._snapshot());
    }

    _undo() {
      if (!this.history.length) return;
      var snap = this.history.pop();
      try {
        this.state.charms = JSON.parse(snap) || [];
      } catch (e) {
        this.state.charms = [];
      }
      this._renderAll();
      this._save();
      this._refreshUndo();
      haptic(8);
    }

    _refreshUndo() {
      if (!this.$undo) return;
      this.$undo.disabled = this.history.length === 0;
    }

    /* ---------------------------------------------------------- */
    /* Persistance                                                 */
    /* ---------------------------------------------------------- */
    _save() {
      try {
        var data = {
          v: STORAGE_VERSION,
          ts: Date.now(),
          base: this.state.base ? { pid: this.state.base.product.id, vid: this.state.base.variant.id } : null,
          f: this._frame ? { l: this._frame.l, t: this._frame.t, w: this._frame.w, h: this._frame.h } : (this._restoredFrame || null),
          note: this.state.note || '',
          charms: this.state.charms.map(function (c) {
            return { pid: c.product.id, vid: c.variant.id, x: c.x, y: c.y, s: c.s };
          }).concat(this._pendingForSave())
        };
        localStorage.setItem(this.storageKey, JSON.stringify(data));
      } catch (e) { /* quota ou navigation privee */ }
    }

    /* Charms restaures pas encore poses : leurs positions sont exprimees
       dans le cadrage d'origine, ramenees au cadrage courant pour la
       sauvegarde. */
    _pendingForSave() {
      var list = this._pendingCharms || [];
      var from = this._restoredFrame;
      var to = this._frame;
      if (!list.length || !from || !to) return list;
      return list.map(function (st) {
        return {
          pid: st.pid, vid: st.vid, s: st.s,
          x: round2(clamp(to.l + (st.x - from.l) * to.w / from.w, 2, 98)),
          y: round2(clamp(to.t + (st.y - from.t) * to.h / from.h, 2, 98))
        };
      });
    }

    _clearStorage() {
      try { localStorage.removeItem(this.storageKey); } catch (e) { /* silencieux */ }
    }

    _restoreFromStorage() {
      var data = null;
      try {
        var raw = localStorage.getItem(this.storageKey);
        if (raw) data = JSON.parse(raw);
      } catch (e) { data = null; }
      if (!data || data.v !== STORAGE_VERSION || !data.ts) return;
      if (Date.now() - data.ts > STORAGE_TTL_MS) {
        this._clearStorage();
        return;
      }
      var restored = false;
      if (data.base) {
        var card = this.querySelector('[data-ab-base][data-product-id="' + data.base.pid + '"]');
        if (card && !card.classList.contains('is-out-of-stock')) {
          var product = parseProduct(card);
          var v = findVariant(product, data.base.vid);
          if (v && v.available) card.dataset.variantId = String(v.id);
          this._selectBase(card, true);
          restored = true;
        }
      }
      if (data.note && this.$note) {
        this.$note.value = data.note;
        this.state.note = data.note;
        this._updateNoteCounter();
      }
      if (data.charms && data.charms.length) {
        this._pendingCharms = data.charms.slice();
        this._pendingCount = data.charms.length;
        /* Cadrage au moment de la sauvegarde : les positions seront
           reprojetees si le bijou n'est plus au meme endroit. */
        this._restoredFrame = (data.f && data.f.w > 0 && data.f.h > 0) ? data.f : null;
        restored = true;
      }
      this._restoredNotice = restored;
    }

    _applyPendingCharms(skipRender) {
      if (!this._pendingCharms || !this._pendingCharms.length) return;
      var self = this;
      var remaining = [];
      var dropped = 0;
      this._pendingCharms.forEach(function (stored) {
        var card = self.querySelector('[data-ab-charm][data-product-id="' + stored.pid + '"]');
        if (!card) { remaining.push(stored); return; }
        if (card.classList.contains('is-out-of-stock')) { dropped++; return; }
        var product = parseProduct(card);
        var v = findVariant(product, stored.vid);
        if (!v || !v.available) { dropped++; return; }
        if (self.state.charms.length >= self.maxCharms) { dropped++; return; }
        self.state.charms.push({
          id: uid(),
          product: { id: product.id, handle: product.handle, title: product.title, image: product.image, multi: product.multi },
          variant: { id: v.id, title: v.title || '', price: v.price || 0, image: v.image || null },
          x: stored.x,
          y: stored.y,
          s: stored.s,
          _restored: true
        });
      });
      var allLoaded = true;
      this.$grids.forEach(function (g) {
        var total = parseInt(g.dataset.total, 10) || 0;
        var rendered = parseInt(g.dataset.rendered, 10) || 0;
        if (total > rendered) allLoaded = false;
      });
      this._pendingCharms = (remaining.length && !allLoaded) ? remaining : null;
      if (!this._pendingCharms) {
        dropped += remaining.length;
        if (this._restoredNotice) {
          this._restoredNotice = false;
          if (dropped > 0) this._showSuccess(this.cfg.msg.unavailableRestored || 'Certains éléments ne sont plus disponibles.', 6000);
          else if (this.state.charms.length) this._showSuccess(this.cfg.msg.restored || 'Composition restaurée.', 4000);
        }
      }
      /* Cadrage deja connu : reprojection immediate, sinon a la fin de l'analyse */
      this._afterFrameRestore();
      this._rehangPending();
      if (this.step === 2 && !skipRender) this._renderAll();
      this._save();
    }

    /* ---------------------------------------------------------- */
    /* Modale variantes                                            */
    /* ---------------------------------------------------------- */
    _openVariantModal(product, size, card) {
      if (!this.$vModal) return;
      var self = this;
      this._vProduct = product;
      this._vSize = size;
      this._vReturnFocus = card || null;
      this._vSelected = null;

      if (this.$vTitle) this.$vTitle.textContent = product.title;
      if (this.$vImg) {
        if (product.image) {
          this.$vImg.src = product.image;
          this.$vImg.hidden = false;
        } else {
          this.$vImg.hidden = true;
        }
      }
      if (this.$vOptions) {
        this.$vOptions.innerHTML = '';
        product.variants.forEach(function (v) {
          var pill = document.createElement('button');
          pill.type = 'button';
          pill.className = 'cds-ab__pill' + (v.available ? '' : ' is-unavailable');
          pill.setAttribute('role', 'radio');
          pill.setAttribute('aria-checked', 'false');
          pill.dataset.abVopt = String(v.id);
          pill.textContent = v.title || product.title;
          if (!v.available) pill.disabled = true;
          self.$vOptions.appendChild(pill);
        });
      }
      if (this.$vConfirmText) this.$vConfirmText.textContent = this.cfg.msg.add || 'Ajouter';
      if (this.$vConfirm) this.$vConfirm.disabled = true;
      if (this.$vPrice) this.$vPrice.textContent = this.cfg.msg.chooseOption || '';

      var first = firstAvailable(product, product.variantId);
      if (first) this._selectModalVariant(first.id);

      this.$vBackdrop.hidden = false;
      this.$vModal.hidden = false;
      this._vOpen = true;
      var focusTarget = this.$vOptions ? this.$vOptions.querySelector('[data-ab-vopt]:not(:disabled)') : null;
      if (focusTarget) focusTarget.focus();
    }

    _selectModalVariant(id) {
      var product = this._vProduct;
      if (!product) return;
      var v = findVariant(product, id);
      if (!v || !v.available) return;
      this._vSelected = v;
      this.$vOptions.querySelectorAll('[data-ab-vopt]').forEach(function (p) {
        var on = parseInt(p.dataset.abVopt, 10) === v.id;
        p.classList.toggle('is-active', on);
        p.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      if (this.$vImg && v.image) this.$vImg.src = v.image;
      if (this.$vPrice) this.$vPrice.textContent = v.price > 0 ? this._money(v.price) : (this.cfg.msg.included || 'Inclus');
      if (this.$vConfirm) this.$vConfirm.disabled = false;
    }

    _confirmModalVariant() {
      if (!this._vProduct || !this._vSelected) return;
      var product = this._vProduct;
      var v = this._vSelected;
      var size = this._vSize;
      this._closeVariantModal();
      this._addCharm(product, v, size);
    }

    _closeVariantModal() {
      if (!this.$vModal) return;
      this.$vBackdrop.hidden = true;
      this.$vModal.hidden = true;
      this._vOpen = false;
      var rf = this._vReturnFocus;
      this._vProduct = null;
      this._vSelected = null;
      this._vReturnFocus = null;
      if (rf && rf.isConnected) {
        try { rf.focus(); } catch (e) { /* silencieux */ }
      }
    }

    /* ---------------------------------------------------------- */
    /* Plein ecran                                                 */
    /* ---------------------------------------------------------- */
    _fillFullscreen() {
      if (!this.$fsInner || !this.$canvas) return;
      this.$fsInner.innerHTML = '';
      var clone = this.$canvas.cloneNode(true);
      clone.removeAttribute('data-ab-canvas');
      clone.classList.remove('is-framing');
      clone.querySelectorAll('[tabindex]').forEach(function (n) { n.removeAttribute('tabindex'); });
      this.$fsInner.appendChild(clone);
    }

    _openFullscreen() {
      if (!this.$fs || !this.$fsInner || !this.$canvas) return;
      this._fillFullscreen();
      this.$fs.hidden = false;
      this._fsOpen = true;
      this._bodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      if (this.$fsClose) this.$fsClose.focus();
      haptic(10);
    }

    _closeFullscreen() {
      if (!this.$fs) return;
      this.$fs.hidden = true;
      this._fsOpen = false;
      document.body.style.overflow = this._bodyOverflow || '';
      if (this.$fullscreen) {
        try { this.$fullscreen.focus(); } catch (e) { /* silencieux */ }
      }
    }

    /* ---------------------------------------------------------- */
    /* Panier                                                      */
    /* ---------------------------------------------------------- */
    _setLoading(on) {
      if (this.$submit) {
        this.$submit.classList.toggle('is-loading', on);
        this.$submit.disabled = on;
      }
      if (this.$loader) this.$loader.hidden = !on;
      this._loading = on;
    }

    _fetchJson(url) {
      return fetch(url, { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); });
    }

    _buildLineItems(group, label) {
      var self = this;
      var cfg = this.cfg;
      var base = this.state.base;
      var ordered = this._ordered();
      var parentProps = { _bundle_group: group, _bundle_role: 'parent' };
      parentProps[cfg.props.composition || 'Composition'] = label;
      parentProps[cfg.baseLabel || 'Base'] = base.product.title + (base.product.multi && base.variant.title ? ' (' + base.variant.title + ')' : '');
      if (ordered.length) {
        var charmName = function (c, i) {
          return (i + 1) + '. ' + c.product.title + (c.product.multi && c.variant.title ? ' (' + c.variant.title + ')' : '');
        };
        if (this._isPair()) {
          var groups = [[], []];
          ordered.forEach(function (c, i) { groups[self._sideOf(c)].push(charmName(c, i)); });
          parentProps[cfg.props.charms || 'Charms'] = [0, 1].filter(function (sd) { return groups[sd].length; }).map(function (sd) {
            return self._sideLabel(sd) + ' : ' + groups[sd].join(', ');
          }).join(' · ');
        } else {
          parentProps[cfg.props.charms || 'Charms'] = ordered.map(charmName).join(' · ');
        }
      }
      var note = (this.state.note || '').trim();
      if (note) parentProps[cfg.props.note || 'Note'] = note;
      if (cfg.previewUrl) {
        try {
          var payload = {
            v: 1,
            t: label,
            o: parseInt(cfg.offsetY, 10) || 0,
            sc: parseInt(cfg.baseScale, 10) || 100,
            f: this._frame ? { l: this._frame.l, t: this._frame.t, w: this._frame.w, h: this._frame.h, b: this._frame.lift || 0 } : undefined,
            b: { h: base.product.handle, v: base.variant.id, n: base.product.title },
            c: ordered.map(function (c) {
              var o = { h: c.product.handle, v: c.variant.id, n: c.product.title, x: round2(c.x), y: round2(c.y), s: round2(c.s) };
              var sd = self._sideOf(c);
              if (sd >= 0) o.sd = self._sideLabel(sd);
              return o;
            })
          };
          var sep = cfg.previewUrl.indexOf('?') === -1 ? '?' : '&';
          parentProps[cfg.props.preview || 'Aperçu visuel'] = window.location.origin + cfg.previewUrl + sep + 'd=' + base64url(JSON.stringify(payload));
        } catch (e) { /* pas de lien, commande normale */ }
      }

      var items = [{ id: parseInt(cfg.parent.variantId, 10), quantity: 1, properties: parentProps }];
      var childProps = { _bundle_group: group, _bundle_role: 'child' };
      childProps[cfg.props.composition || 'Composition'] = label;

      var baseProps = {};
      for (var k in childProps) baseProps[k] = childProps[k];
      items.push({ id: parseInt(base.variant.id, 10), quantity: 1, properties: baseProps });

      var byVariant = {};
      var order = [];
      ordered.forEach(function (c) {
        var vid = String(c.variant.id);
        if (!byVariant[vid]) {
          byVariant[vid] = { id: parseInt(vid, 10), quantity: 0, properties: {}, handle: c.product.handle, title: c.product.title };
          for (var k2 in childProps) byVariant[vid].properties[k2] = childProps[k2];
          order.push(vid);
        }
        byVariant[vid].quantity += 1;
      });
      order.forEach(function (vid) { items.push(byVariant[vid]); });
      return items;
    }

    /* Verifie la disponibilite de chaque variante via /products/handle.js.
       En cas d'echec reseau, on laisse Shopify trancher a l'ajout. */
    _checkStock(items) {
      var self = this;
      var handles = {};
      if (this.cfg.parent && this.cfg.parent.handle) handles[this.cfg.parent.handle] = true;
      handles[this.state.base.product.handle] = true;
      this.state.charms.forEach(function (c) { handles[c.product.handle] = true; });
      var list = Object.keys(handles).filter(Boolean);
      return Promise.all(list.map(function (h) {
        return self._fetchJson('/products/' + encodeURIComponent(h) + '.js').catch(function () { return null; });
      })).then(function (products) {
        var byVid = {};
        products.forEach(function (p) {
          if (!p || !p.variants) return;
          p.variants.forEach(function (v) { byVid[String(v.id)] = { v: v, title: p.title }; });
        });
        for (var i = 0; i < items.length; i++) {
          var it = items[i];
          var info = byVid[String(it.id)];
          if (!info) continue;
          var v = info.v;
          var name = info.title + (v.title && v.title !== 'Default Title' ? ' (' + v.title + ')' : '');
          /* /products/x.js n'expose pas la quantite en stock : seule la
             disponibilite est verifiee ici. Une quantite insuffisante est
             detectee apres l'ajout (voir _verifyAdded). */
          if (v.available === false) {
            return { ok: false, message: '« ' + name + ' » n’est plus disponible.' };
          }
        }
        return { ok: true };
      }).catch(function () { return { ok: true }; });
    }

    _addToCart() {
      var self = this;
      var cfg = this.cfg;
      if (cfg.cartOff || this._loading) return;
      if (!this.state.base) {
        this._showError(cfg.msg.chooseBase || 'Choisissez votre base.');
        return;
      }
      if (cfg.requireCharm && !this.state.charms.length) {
        this._showError(cfg.msg.minCharm || 'Ajoutez au moins un charm.');
        return;
      }
      this._hideMessages();
      this._setLoading(true);

      var group = 'ab-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
      var requested = null;

      this._fetchJson(cfg.cartUrl ? cfg.cartUrl + '.js' : '/cart.js')
        .catch(function () { return null; })
        .then(function (c) {
          var n = 0;
          ((c && c.items) || []).forEach(function (it) {
            if (it.product_id === cfg.parent.id && it.properties && it.properties._bundle_role === 'parent') n++;
          });
          var label = (cfg.parent.title || 'Composition') + (n > 0 ? ' ' + (n + 1) : '');
          var items = self._buildLineItems(group, label);
          requested = {};
          items.forEach(function (it) { requested[String(it.id)] = (requested[String(it.id)] || 0) + it.quantity; });
          return self._checkStock(items).then(function (res) {
            if (!res.ok) throw { description: res.message, stock: true };
            var payload = { items: items.map(function (it) { return { id: it.id, quantity: it.quantity, properties: it.properties }; }) };
            var sids = (window.CDSBuilders && window.CDSBuilders.cartSectionIds) ? window.CDSBuilders.cartSectionIds() : [];
            if (sids.length) {
              payload.sections = sids.join(',');
              payload.sections_url = window.location.pathname;
            }
            return fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: JSON.stringify(payload)
            });
          });
        })
        .then(function (r) {
          if (!r.ok) {
            return r.json().then(function (e) { throw e; }, function () { throw { message: 'HTTP ' + r.status }; });
          }
          return r.json();
        })
        .then(function (d) {
          if (d && d.status && d.status >= 400) throw d;
          if (!self._verifyAdded(d, requested)) throw { partial: true };
          self._onAdded(d);
        })
        .catch(function (err) {
          var fallbackMsg = cfg.msg.cartError || 'Erreur lors de l’ajout au panier.';
          /* Erreur reseau du navigateur (« Failed to fetch ») : message clair */
          var msg = (err instanceof Error) ? fallbackMsg : ((err && (err.description || err.message)) || fallbackMsg);
          if (err && err.partial) msg = cfg.msg.partialStock || msg;
          if (err && err.stock) {
            self._showError(msg);
            return;
          }
          /* Refus ou ajout incomplet : la composition ne doit pas rester
             a moitie dans le panier. */
          return self._rollbackGroup(group).then(function (ok) {
            self._showError(ok ? msg : (cfg.msg.rollbackFailed || msg));
          });
        })
        .then(function () { self._setLoading(false); });
    }

    /* Chaque ligne demandee doit etre revenue avec sa quantite complete
       (Shopify peut plafonner une quantite au stock disponible). */
    _verifyAdded(d, requested) {
      if (!d || !Array.isArray(d.items) || !requested) return true;
      var got = {};
      d.items.forEach(function (it) {
        var k = String(it.variant_id || it.id);
        got[k] = (got[k] || 0) + (parseInt(it.quantity, 10) || 0);
      });
      for (var k in requested) {
        if (Object.prototype.hasOwnProperty.call(requested, k) && (got[k] || 0) < requested[k]) return false;
      }
      return true;
    }

    /* Retire du panier toutes les lignes de la composition (meme groupe).
       Resout true si le panier est propre, false si le retrait a echoue. */
    _rollbackGroup(group) {
      var self = this;
      var cfg = this.cfg;
      return this._fetchJson(cfg.cartUrl ? cfg.cartUrl + '.js' : '/cart.js')
        .then(function (c) {
          var updates = {};
          var n = 0;
          ((c && c.items) || []).forEach(function (it) {
            if (it.properties && it.properties._bundle_group === group) { updates[it.key] = 0; n++; }
          });
          if (!n) return true;
          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ updates: updates })
          }).then(function (r) { return r.ok ? r.json() : null; }).then(function (cart) {
            if (!cart) return false;
            try {
              if (window.CDSBuilders && window.CDSBuilders.dispatchCartUpdate) {
                window.CDSBuilders.dispatchCartUpdate('cds-atelier-bijou', {}, cfg.parent.id, cart.item_count || 0);
              }
            } catch (e) { /* silencieux */ }
            return true;
          });
        })
        .catch(function () { return false; });
    }

    _onAdded(d) {
      var self = this;
      var cfg = this.cfg;
      var sections = (d && d.sections) || {};
      var finish = function (count) {
        try {
          if (window.CDSBuilders && window.CDSBuilders.dispatchCartUpdate) {
            window.CDSBuilders.dispatchCartUpdate('cds-atelier-bijou', sections, cfg.parent.id, count);
          }
        } catch (e) { /* silencieux */ }
        if (window.CDSBuilders && window.CDSBuilders.openCartDrawer) {
          window.CDSBuilders.openCartDrawer();
        } else {
          window.location.href = cfg.cartUrl || '/cart';
        }
      };
      this._fetchJson(cfg.cartUrl ? cfg.cartUrl + '.js' : '/cart.js')
        .then(function (c) { finish(c && c.item_count ? c.item_count : 1); })
        .catch(function () { finish(1); });

      /* Composition ajoutee : on repart propre (la base reste choisie) */
      this._clearStorage();
      this.history = [];
      this._refreshUndo();
      this.state.charms = [];
      this.state.note = '';
      if (this.$note) this.$note.value = '';
      this._updateNoteCounter();
      this._renderAll();
      this._showSuccess(cfg.msg.success || 'Ajouté au panier.', 5000);
      haptic([12, 40, 18]);
    }
  }

  customElements.define('cds-atelier-bijou', CdsAtelierBijou);
})();
